<template>
  <div class="historic-evaluation-summary">
    <!-- Summary Card -->
    <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <div class="flex items-center mb-3">
        <i class="pi pi-history text-amber-600 text-xl mr-2"></i>
        <h3 class="text-lg font-semibold text-amber-800">Riwayat Evaluasi Sebelumnya</h3>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span class="text-gray-600">Total Peserta:</span>
          <span class="font-semibold ml-1">{{ summarySnapshot?.total_peserta || 0 }}</span>
        </div>
        <div>
          <span class="text-gray-600">Lulus Evaluasi:</span>
          <span class="font-semibold ml-1 text-green-600">{{ summarySnapshot?.lulus_evaluasi || 0 }}</span>
        </div>
        <div>
          <span class="text-gray-600">Pemenang:</span>
          <span class="font-semibold ml-1">{{ summarySnapshot?.pemenang || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-600">Tanggal Arsip:</span>
          <span class="font-semibold ml-1">{{ formatDate(summarySnapshot?.tanggal_backup) }}</span>
        </div>
      </div>
    </div>

    <!-- Participants Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="px-4 py-3 bg-gray-50 border-b">
        <h4 class="font-semibold text-gray-700">Daftar Peserta & Evaluasi</h4>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">No</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Penyedia</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Penawaran</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status Evaluasi</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detail Penilaian</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keterangan</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr 
              v-for="(kepersertaan, index) in sortedKepersertaanList" 
              :key="kepersertaan.id"
              :class="kepersertaan.is_lulus_evaluasi ? 'bg-green-50' : 'bg-gray-50'"
            >
              <td class="px-4 py-3 text-sm">{{ index + 1 }}</td>
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-gray-900">
                  {{ kepersertaan.penyedia?.nama_perusahaan || '-' }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ kepersertaan.penyedia?.npwp_perusahaan || '' }}
                </div>
              </td>
              <td class="px-4 py-3 text-right text-sm font-medium">
                {{ formatCurrency(calculateTotalPenawaran(kepersertaan)) }}
              </td>
              <td class="px-4 py-3 text-center">
                <div class="flex items-center justify-center">
                  <span 
                    v-if="kepersertaan.is_pemenang" 
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-2"
                  >
                    <i class="pi pi-trophy mr-1"></i> Pemenang
                  </span>
                  <span 
                    :class="kepersertaan.is_lulus_evaluasi ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" 
                    class="px-2 py-1 text-xs font-semibold rounded-full"
                  >
                    {{ kepersertaan.is_lulus_evaluasi ? 'LULUS' : 'TIDAK LULUS' }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div v-if="kepersertaan.penilaian_penyedia_dokumen_pengadaan?.length > 0" class="text-sm">
                  <div 
                    v-for="(penilaian, pidx) in kepersertaan.penilaian_penyedia_dokumen_pengadaan" 
                    :key="pidx" 
                    class="mb-1"
                  >
                    <span 
                      :class="penilaian.is_lulus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" 
                      class="px-2 py-0.5 text-xs font-medium rounded-full"
                    >
                      {{ penilaian.is_lulus ? 'Lulus' : 'Tidak Lulus' }}
                    </span>
                    <span class="text-xs text-gray-500 ml-1">
                      {{ penilaian.bobot_penilaian_dokumen_pengadaan?.jenis_bobot_penilaian?.nama_jenis_bobot || 
                         penilaian.bobot_penilaian_dokumen_pengadaan?.kode_bobot || '-' }}
                    </span>
                    <span class="text-xs text-gray-400 ml-1">({{ penilaian.nilai }})</span>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-500">-</div>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600">
                {{ kepersertaan.keterangan_penilaian || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Detail Penawaran Modal Trigger -->
    <div class="mt-4 text-right">
      <Button 
        v-if="selectedKepersertaan"
        label="Lihat Detail Penawaran" 
        icon="pi pi-eye" 
        severity="info" 
        size="small"
        @click="showDetailModal = true"
      />
    </div>

    <!-- Detail Modal -->
    <Dialog 
      v-model:visible="showDetailModal" 
      header="Detail Penawaran Historis" 
      :style="{ width: '80vw' }" 
      :modal="true"
    >
      <div v-if="selectedKepersertaan" class="space-y-4">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 class="font-semibold text-blue-800 mb-2">
            {{ selectedKepersertaan.penyedia?.nama_perusahaan }}
          </h4>
          <div class="grid grid-cols-2 gap-2 text-sm">
            <div><span class="text-gray-600">NPWP:</span> {{ selectedKepersertaan.penyedia?.npwp_perusahaan || '-' }}</div>
            <div><span class="text-gray-600">Alamat:</span> {{ selectedKepersertaan.penyedia?.alamat_perusahaan || '-' }}</div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Nama Barang/Jasa</th>
                <th class="px-4 py-2 text-center text-xs font-medium text-gray-500">Volume</th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500">Harga Satuan</th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr 
                v-for="detail in selectedKepersertaan.detail_penawaran_kepersertaan_pengadaan" 
                :key="detail.id"
              >
                <td class="px-4 py-2 text-sm">
                  {{ detail.nama_barang_jasa }}
                  <div v-if="detail.merk || detail.tipe" class="text-xs text-gray-500">
                    {{ [detail.merk, detail.tipe].filter(Boolean).join(' - ') }}
                  </div>
                </td>
                <td class="px-4 py-2 text-sm text-center">{{ detail.volume }} {{ detail.satuan }}</td>
                <td class="px-4 py-2 text-sm text-right">{{ formatCurrency(detail.harga_satuan) }}</td>
                <td class="px-4 py-2 text-sm text-right font-medium">{{ formatCurrency(detail.volume * detail.harga_satuan) }}</td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td colspan="3" class="px-4 py-2 text-sm font-semibold text-right">Total Penawaran:</td>
                <td class="px-4 py-2 text-sm font-semibold text-right">
                  {{ formatCurrency(calculateTotalPenawaran(selectedKepersertaan)) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'

export default {
  name: 'HistoricEvaluationSummary',

  components: {
    Button,
    Dialog
  },

  props: {
    riwayat: {
      type: Object,
      required: true
    },
    dokumenPengadaan: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      showDetailModal: false,
      selectedKepersertaan: null
    }
  },

  computed: {
    summarySnapshot() {
      return this.riwayat?.summary_snapshot || {}
    },

    kepersertaanList() {
      return this.riwayat?.kepersertaan_pengadaan_data || []
    },

    sortedKepersertaanList() {
      if (!this.kepersertaanList || this.kepersertaanList.length === 0) return []
      
      return [...this.kepersertaanList].sort((a, b) => {
        // Sort by evaluation status (lulus first)
        const aLulus = a.is_lulus_evaluasi ? 1 : 0
        const bLulus = b.is_lulus_evaluasi ? 1 : 0
        
        if (aLulus !== bLulus) {
          return bLulus - aLulus
        }
        
        // Then by total penawaran (lowest first)
        const aTotalPenawaran = this.calculateTotalPenawaran(a)
        const bTotalPenawaran = this.calculateTotalPenawaran(b)
        
        return aTotalPenawaran - bTotalPenawaran
      })
    }
  },

  methods: {
    calculateTotalPenawaran(kepersertaan) {
      if (!kepersertaan.detail_penawaran_kepersertaan_pengadaan) return 0
      return kepersertaan.detail_penawaran_kepersertaan_pengadaan.reduce(
        (sum, item) => sum + (item.volume * item.harga_satuan), 0
      )
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return 'Rp 0'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    viewDetail(kepersertaan) {
      this.selectedKepersertaan = kepersertaan
      this.showDetailModal = true
    }
  }
}
</script>

<style scoped>
.historic-evaluation-summary {
  font-family: inherit;
}
</style>


